import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Maximize2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { tertiaryText } from '../../../styling/stylingHelper';
import { getCategoryDisplayName, getSubCategoryDisplayName, getCategoryTextColor } from '../../orderhistory/categoryUtils';
import { RESTAURANT_COLORS } from '../../../utils/restaurantColors';
import { CardWithHeader } from '../../ui/CardWithHeader';
import { ExpandModal } from '../../ui/ExpandModal';
import { TabbedSections } from '../../ui/TabbedSections';
import { DonutWithLegend } from '../../ui/DonutWithLegend';
import { NoDataInPeriod } from '../../ui/NoDataInPeriod';
import { RankedValueBars, RankedBarRow } from './RankedValueBars';
import { CountReviewProduct, CountReviewResponse } from '../../../services/inventory/inventoryService';

export type CategoryMode = 'inventory' | 'product';

export type InventoryCategoryRow = CountReviewResponse['categoriesInventory'][number];
export type ProductCategoryRow = CountReviewResponse['categoriesProduct'][number];

/** A category selection emitted by click-to-filter (section detail). */
export interface CategorySelection {
  mode: CategoryMode;
  /** Stable row id (used for highlight). */
  rowId: string;
  /** Inventory mode: the frozen category id (null = "Uncategorized" bucket). */
  categoryId: string | null;
  /** Inventory mode: the display name (disambiguates the null-id bucket). */
  inventoryName: string;
  /** Product mode: the product-category key. */
  productKey: string;
  /** Translated label (for a "filtered by …" affordance). */
  label: string;
}

const invRowId = (c: InventoryCategoryRow): string =>
  c.categoryId ? `inv:${c.categoryId}` : `inv-name:${c.name}`;
const prodRowId = (c: ProductCategoryRow): string => `prod:${c.key}`;

/** Preview list height cap before scrolling kicks in (same as ByRestaurantCard). */
const PREVIEW_MAX_HEIGHT = 318;

/**
 * Group a product list into the inventory- and product-category breakdowns the card
 * consumes, scoped to whatever products are passed (e.g. one section). Mirrors the
 * server's `count-review` aggregation shapes so it can stand in for the payload arrays.
 */
export function buildCategoryBreakdown(products: CountReviewProduct[]): {
  inventory: InventoryCategoryRow[];
  product: ProductCategoryRow[];
} {
  const total = products.reduce((sum, p) => sum + p.value, 0);
  const invMap = new Map<string, InventoryCategoryRow>();
  const prodMap = new Map<string, ProductCategoryRow>();

  for (const p of products) {
    // Inventory category (frozen). All null-id products merge into one "Uncategorized"
    // bucket, mirroring the server aggregation (invCatId ?? UNCAT_KEY).
    const invKey = p.inventoryCategoryId ?? '__uncategorized__';
    let inv = invMap.get(invKey);
    if (!inv) {
      inv = {
        categoryId: p.inventoryCategoryId,
        name: p.inventoryCategoryName ?? '',
        totalValue: 0,
        share: 0,
        productCount: 0,
        belowMinCount: 0,
      };
      invMap.set(invKey, inv);
    }
    inv.totalValue += p.value;
    inv.productCount += 1;
    if (p.belowMinimum) inv.belowMinCount += 1;

    // Product category (live). `productCategory` is already server-resolved: a custom
    // ingredient with a live enum category carries that category, else 'custom'; null →
    // 'uncategorized'. Mirror the server bucketing exactly (no extra source branch).
    const prodKey = p.productCategory ?? 'uncategorized';
    let prod = prodMap.get(prodKey);
    if (!prod) {
      prod = {
        key: prodKey,
        mainCategory: p.mainCategory,
        totalValue: 0,
        share: 0,
        productCount: 0,
        belowMinCount: 0,
        subCategories: [],
      };
      prodMap.set(prodKey, prod);
    }
    prod.totalValue += p.value;
    prod.productCount += 1;
    if (p.belowMinimum) prod.belowMinCount += 1;

    // Subcategory bucket within the product category (first subCategory wins; none → null bucket),
    // mirroring the server aggregation.
    const subKey = p.subCategories?.[0] ?? null;
    let sub = prod.subCategories.find((s) => s.key === subKey);
    if (!sub) {
      sub = { key: subKey, totalValue: 0, share: 0, productCount: 0, belowMinCount: 0 };
      prod.subCategories.push(sub);
    }
    sub.totalValue += p.value;
    sub.productCount += 1;
    if (p.belowMinimum) sub.belowMinCount += 1;
  }

  const withShare = <T extends { totalValue: number; share: number }>(rows: T[]): T[] => {
    rows.forEach((r) => {
      r.share = total > 0 ? r.totalValue / total : 0;
    });
    return rows.sort((a, b) => b.totalValue - a.totalValue);
  };

  const product = withShare(Array.from(prodMap.values()));
  product.forEach((p) => withShare(p.subCategories));

  return {
    inventory: withShare(Array.from(invMap.values())),
    product,
  };
}

export interface ValueByCategoryCardProps {
  categoriesInventory: InventoryCategoryRow[];
  categoriesProduct: ProductCategoryRow[];
  mode: CategoryMode;
  onModeChange: (mode: CategoryMode) => void;
  emptyText?: string;
  /** Row id of the active click-to-filter selection (highlights the row). */
  activeRowId?: string | null;
  /** When set, rows become clickable and emit a selection (section-detail filtering). */
  onSelect?: (selection: CategorySelection) => void;
  /**
   * 'pie' (count review): dashboard-style donut + legend in the card; the expand modal
   * shows the donut with the detailed bars below it. 'bars' (default, section detail):
   * the ranked bar rows, which stay clickable for click-to-filter.
   */
  display?: 'pie' | 'bars';
  /**
   * Whether category sublines show the red "X below minimum" part. The section detail
   * page turns this off: the minimum is a cross-section total, so a per-section
   * below-minimum count is misleading there (group-level pages keep it).
   */
  showBelowMin?: boolean;
  /** Optional `data-onboarding` id on the card title. */
  titleOnboardingId?: string;
  /** Prefix for inventory/product mode tabs (`{prefix}-{value}`). */
  modeOnboardingPrefix?: string;
}

/**
 * "Value by category" card with an inventory-category ↔ product-category toggle.
 * Shared by the Inventory Count Review page (P2) and the section detail page (P3).
 * On the section page rows are click-to-filter (pass `onSelect`); on the main page
 * they are read-only.
 */
export const ValueByCategoryCard: React.FC<ValueByCategoryCardProps> = ({
  categoriesInventory,
  categoriesProduct,
  mode,
  onModeChange,
  emptyText,
  activeRowId,
  onSelect,
  display = 'bars',
  showBelowMin = true,
  titleOnboardingId,
  modeOnboardingPrefix,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  // Kept in step with productCategoryLabel (inventoryReviewShared) — same sentinel buckets,
  // same labels; only the null case differs.
  const productCatLabel = (key: string, mainCategory: string | null): string => {
    if (key === 'custom') return t('inventory.customBucket');
    if (key === 'subrecipe') return t('inventory.subrecipeBucket');
    if (key === 'uncategorized') return t('inventory.uncategorizedBucket');
    return getCategoryDisplayName(key, mainCategory ?? undefined) ?? key;
  };


  const productCountSubline = (productCount: number): React.ReactNode => (
    <span className={cn(tertiaryText)}>{t('inventory.productCountLabel', { count: productCount })}</span>
  );

  const belowMinRightPill = (belowMinCount: number): React.ReactNode => {
    if (!showBelowMin) return undefined;
    if (belowMinCount > 0) {
      return (
        <span className="text-[12px] leading-[18px] font-medium px-1 sm:px-2 py-1 rounded-[5px] whitespace-nowrap"
          style={{ backgroundColor: '#D92D201A', color: '#D92D20' }}>
          {t('inventory.belowMinimumLabel', { count: belowMinCount })}
        </span>
      );
    }
    return (
      <span className="text-[12px] leading-[18px] font-medium px-1 sm:px-2 py-1 rounded-[5px] whitespace-nowrap"
        style={{ backgroundColor: '#2E90FA1A', color: '#2E90FA' }}>
        {t('inventory.belowMinimumLabel', { count: 0 })}
      </span>
    );
  };

  const rows: RankedBarRow[] =
    mode === 'inventory'
      ? categoriesInventory.map((c) => {
          const label = c.name || t('inventory.uncategorizedBucket');
          const rowId = invRowId(c);
          return {
            id: rowId,
            label,
            value: c.totalValue,
            share: c.share,
            subline: productCountSubline(c.productCount),
            rightPill: belowMinRightPill(c.belowMinCount),
            active: activeRowId === rowId,
            onClick: onSelect
              ? () =>
                  onSelect({
                    mode: 'inventory',
                    rowId,
                    categoryId: c.categoryId,
                    inventoryName: c.name,
                    productKey: '',
                    label,
                  })
              : undefined,
          };
        })
      : categoriesProduct.map((c) => {
          const label = productCatLabel(c.key, c.mainCategory);
          const rowId = prodRowId(c);
          // Subcategory sub-rows only when the category has at least one real subcategory
          // bucket (a lone "no subcategory" bucket would just duplicate the parent row).
          const hasRealSub = c.subCategories.some((s) => s.key !== null);
          const subRows: RankedBarRow[] | undefined = hasRealSub
            ? c.subCategories.map((s) => ({
                id: `${rowId}:sub:${s.key ?? 'none'}`,
                label: s.key ? (getSubCategoryDisplayName(s.key) ?? s.key) : t('inventory.noSubcategoryBucket'),
                value: s.totalValue,
                share: s.share,
                subline: productCountSubline(s.productCount),
                rightPill: belowMinRightPill(s.belowMinCount),
              }))
            : undefined;
          return {
            id: rowId,
            label,
            value: c.totalValue,
            share: c.share,
            subline: productCountSubline(c.productCount),
            rightPill: belowMinRightPill(c.belowMinCount),
            subRows,
            active: activeRowId === rowId,
            onClick: onSelect
              ? () =>
                  onSelect({
                    mode: 'product',
                    rowId,
                    categoryId: null,
                    inventoryName: '',
                    productKey: c.key,
                    label,
                  })
              : undefined,
          };
        });

  const toggle = (
    <TabbedSections
      options={[
        { value: 'inventory', label: t('inventory.catModeInventory') },
        { value: 'product', label: t('inventory.catModeProduct') },
      ]}
      value={mode}
      onChange={(v) => onModeChange(v as CategoryMode)}
      isTimeOptionSelection={true}
      onboardingId={modeOnboardingPrefix}
    />
  );

  const maximizeButton = (
    <button
      type="button"
      onClick={() => setIsExpanded(true)}
      title={t('dashboard.maximize')}
      aria-label={t('dashboard.maximize')}
      className="p-1.5 rounded-md text-textSecondary dark:text-textSecondary-dark hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
    >
      <Maximize2 className="w-4 h-4" />
    </button>
  );

  // Slice colors: enum categories use their shared category color; user-defined
  // inventory categories have no stable color, so assign from the chart palette by rank.
  const sliceColors =
    mode === 'inventory'
      ? rows.map((_, i) => RESTAURANT_COLORS[i % RESTAURANT_COLORS.length])
      : categoriesProduct.map((c) => getCategoryTextColor(c.key));
  const pieItems = rows.map((r, i) => ({ label: r.label, percentage: r.share * 100, color: sliceColors[i] }));

  const emptyState = emptyText ? (
    <p className="text-[14px] text-textSecondary dark:text-textSecondary-dark text-center py-6">{emptyText}</p>
  ) : (
    <NoDataInPeriod />
  );

  if (display === 'pie') {
    return (
      <>
        <CardWithHeader title={t('inventory.valueByCategory')} titleOnboardingId={titleOnboardingId} headerActions={toggle} headerActionsRight={maximizeButton}>
          {rows.length === 0 ? (
            emptyState
          ) : (
            <DonutWithLegend
              items={pieItems}
              tooltipId="inventory-value-category-chartjs-tooltip"
              heightClass="h-[200px] sm:h-[240px]"
              legendMaxHeightClass="max-h-[180px]"
            />
          )}
        </CardWithHeader>

        <ExpandModal isOpen={isExpanded} onClose={() => setIsExpanded(false)} title={t('inventory.valueByCategory')} maxWidth="max-w-2xl">
          <div className="flex flex-col">
            <div className="flex justify-end mb-2">{toggle}</div>
            {rows.length === 0 ? (
              emptyState
            ) : (
              <>
                <DonutWithLegend
                  items={pieItems}
                  tooltipId="inventory-value-category-chartjs-tooltip"
                  heightClass="h-[360px] sm:h-[440px]"
                  legendMaxWidthClass="max-w-full"
                  legendMaxHeightClass="max-h-[260px]"
                />
                <div className="mt-6">
                  <RankedValueBars rows={rows} emptyText={emptyText} />
                </div>
              </>
            )}
          </div>
        </ExpandModal>
      </>
    );
  }

  return (
    <>
      <CardWithHeader title={t('inventory.valueByCategory')} headerActions={toggle} headerActionsRight={maximizeButton}>
        <div className="w-full overflow-y-auto custom-scrollbar" style={{ maxHeight: PREVIEW_MAX_HEIGHT }}>
          <RankedValueBars rows={rows} emptyText={emptyText} />
        </div>
      </CardWithHeader>

      <ExpandModal isOpen={isExpanded} onClose={() => setIsExpanded(false)} title={t('inventory.valueByCategory')} maxWidth="max-w-2xl">
        <RankedValueBars rows={rows} emptyText={emptyText} />
      </ExpandModal>
    </>
  );
};

export default ValueByCategoryCard;
